import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'

import Button from 'components/button'
import { useAuthProvider } from 'contexts/auth'
import { Contract, ContractLink, FeatureLink } from 'schema/contract'
import { UserRole } from 'schema/user'
import { convertPrice, formatedDate } from 'utils'
import useFetchChatRoom from '../../../pages/contract/hooks/useFetchChatRoom'

interface ContractListItemProps {
  type: string | null
  data: ContractLink
}

interface FeatureListItemProps {
  type: string | null
  data: FeatureLink
  image: string
  imageAlt: string
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid ${(p) => p.theme.BORDER};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 12px;
  width: 362px;
  box-sizing: border-box;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    width: 100%;
  }
`

const Contents = styled.div`
  width: 100%;
  margin-bottom: 20px;
`

const PropertyTitle = styled.h3`
  font-size: ${(p) => p.theme.font.size.m};
  font-weight: 600;
  color: #333333;
  margin: 0 0;
  line-height: 1.4;
  min-height: 56px;
`

const PropertyInfo = styled.div`
  margin-bottom: 0;
`

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  height: calc(72px - 16px);
  padding: 8px 0;
  border-bottom: 1px solid ${(p) => p.theme.BORDER};
`

const InfoIcon = styled.div<{
  iconType: 'money' | 'calendar' | 'owner' | 'tag'
}>`
  width: 12px;
  height: 12px;
  margin-right: 12px;
  flex-shrink: 0;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;

  ${(p) => {
    switch (p.iconType) {
      case 'money':
        return css`
          background-image: url('/image/price.svg');
        `
      case 'calendar':
        return css`
          background-image: url('/image/calendar.svg');
        `
      case 'owner':
        return css`
          background-image: url('/image/owner.svg');
        `
      case 'tag':
        return css`
          background-image: url('/image/tag.svg');
        `
      default:
        return css`
          background-color: #cccccc;
          border-radius: 50%;
        `
    }
  }}
`

const InfoLabel = styled.span`
  font-size: ${(p) => p.theme.font.size.m};
  color: #666666;
  margin-right: auto;
  min-width: 80px;
`

const InfoValue = styled.span`
  font-size: ${(p) => p.theme.font.size.m};
  color: #333333;
  font-weight: 500;

  &.price {
    font-weight: 500;
  }

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    font-size: 14px;
  }
`

const TypeLogo = styled.div<{ logoType: 'suumo' | 'homes' | 'athome' }>`
  width: 80px;
  height: 30px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center right;
  background-image: url('/image/${(p) => p.logoType}.png');
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
`

const ActionButton = styled(Button)<{
  variant?: 'chat' | 'info'
  unread?: number | false
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  justify-content: center;
  text-decoration: none;
  border: none;
  box-shadow: none;
  justify-content: center;
  position: relative;
  padding-left: 24px;

  ${(p) =>
    p.variant === 'chat'
      ? css`
          background-color: #ffffff;
          color: #666666;
          border: 1px solid ${(p) => p.theme.BORDER_BUTTON};

          &:hover {
            background-color: #f0f0f0;
          }
        `
      : css`
          background-color: ${p.theme.MAIN_BACKGROUND_V2};
          color: black;
          border: 1px solid ${p.theme.MAIN_BORDER_V2};

          &:hover {
            background-color: ${p.theme.MAIN_V2};
          }
        `}
`

const ButtonIcon = styled.div<{
  variant?: 'chat' | 'info'
  hasUnread?: boolean
}>`
  width: 20px;
  height: 20px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);

  ${(p) => {
    if (p.variant === 'chat') {
      return css`
        background-image: url('/image/${p.hasUnread
          ? 'chat-unread'
          : 'chat'}.svg');
      `
    } else {
      return css`
        background-image: url('/image/diagram.svg');
      `
    }
  }}
`

const ButtonLabel = styled.span`
  vertical-align: middle;
  padding: 0;
  margin: 0;
  line-height: 16px;
`

export const ContractListItem = ({ type, data }: ContractListItemProps) => {
  const { claims } = useAuthProvider()
  const { unreadCnt } = useFetchChatRoom(data as Contract, false)
  const { termStartAt, termEndAt } = data

  return (
    <Container>
      <Contents>
        <PropertyTitle>{data.name}</PropertyTitle>
        <PropertyInfo>
          <InfoRow>
            <InfoIcon iconType="money" />
            <InfoLabel>物件価格</InfoLabel>
            <InfoValue className="price">
              {convertPrice(data.price)}円
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoIcon iconType="calendar" />
            <InfoLabel>契約期間</InfoLabel>
            <InfoValue>
              {formatedDate(termStartAt)} - {formatedDate(termEndAt)}
            </InfoValue>
          </InfoRow>
          {claims?.role === UserRole.AGENT && (
            <InfoRow>
              <InfoIcon iconType="owner" />
              <InfoLabel>売主名</InfoLabel>
              <InfoValue>{data.ownerName}</InfoValue>
            </InfoRow>
          )}
        </PropertyInfo>
      </Contents>
      <ButtonContainer>
        <ActionButton
          as={Link}
          variant="chat"
          to={data.toChat}
          unread={unreadCnt}
        >
          <ButtonIcon variant="chat" hasUnread={!!unreadCnt} />
          <ButtonLabel> チャット </ButtonLabel>
        </ActionButton>
        <ActionButton
          as={Link}
          variant="info"
          to={`${data.toDetail}?type=${type}`}
        >
          <ButtonIcon variant="info" />
          <ButtonLabel> 物件情報 </ButtonLabel>
        </ActionButton>
      </ButtonContainer>
    </Container>
  )
}

export const FeatureListItem = ({
  type,
  data,
  image: _image,
  imageAlt: _imageAlt,
}: FeatureListItemProps) => {
  const { termStartAt, termEndAt } = data

  return (
    <Container>
      <Contents>
        <PropertyTitle>{data.name}</PropertyTitle>
        <PropertyInfo>
          <InfoRow>
            <InfoIcon iconType="tag" />
            <InfoLabel>媒体種別</InfoLabel>
            <TypeLogo logoType={data.type} />
          </InfoRow>
          <InfoRow>
            <InfoIcon iconType="calendar" />
            <InfoLabel>契約期間</InfoLabel>
            <InfoValue>
              {formatedDate(termStartAt)} - {formatedDate(termEndAt)}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoIcon iconType="money" />
            <InfoLabel>物件価格</InfoLabel>
            <InfoValue className="price">
              {convertPrice(data.price)}円
            </InfoValue>
          </InfoRow>
        </PropertyInfo>
      </Contents>
      <ButtonContainer>
        <ActionButton
          as={Link}
          variant="info"
          to={`${data.toDetail}?type=${type}`}
        >
          <ButtonIcon variant="info" />
          <ButtonLabel> 物件情報 </ButtonLabel>
        </ActionButton>
      </ButtonContainer>
    </Container>
  )
}
